/**
 * 把审批工具参数翻译成作者能看懂的“操作 / 目标 / 字段”。
 * 这里只做纯展示，不参与权限判断。
 */

export interface ApprovalLike {
  toolName: string;
  reason?: string;
  argumentsParsed?: unknown;
  argumentsText?: string;
}

export interface ApprovalField {
  label: string;
  value: string;
  long?: boolean;
}

export interface ApprovalItem {
  action: string;
  target: string;
  fields: ApprovalField[];
}

export interface ApprovalSummary {
  title: string;
  toolLabel: string;
  description: string;
  items: ApprovalItem[];
}

const TARGETS: Record<string, string> = {
  character: '人物',
  faction: '势力',
  setting: '设定',
  item: '物品',
  volume: '分卷',
  outline: '大纲',
  chapter: '章节',
  chapter_content: '章节正文',
};

const ACTIONS: Record<string, string> = {
  create: '新增',
  update: '修改',
  delete: '删除',
};

const FIELD_LABELS: Record<string, string> = {
  title: '标题',
  name: '名称',
  content: '正文',
  summary: '摘要',
  description: '描述',
  status: '状态',
  age: '年龄',
  faction_id: '所属势力',
  parent_faction_id: '上级势力',
  strength: '实力',
  type: '类型',
  order_index: '顺序',
  volume_id: '所属分卷',
  chapter_number: '章节号',
  target_words: '目标字数',
};

function text(value: unknown, max = 600): string {
  if (value === null || value === undefined) return '';
  const raw = typeof value === 'string' ? value : JSON.stringify(value, null, 2);
  return raw.length > max ? `${raw.slice(0, max)}…（共 ${raw.length} 字）` : raw;
}

function targetLabel(target: string): string {
  return TARGETS[target] ?? target;
}

function asActions(args: unknown): Array<{ action: string; target: string; targetId?: string; data?: Record<string, unknown> }> {
  if (!args || typeof args !== 'object') return [];
  const box = args as { actions?: unknown; action?: unknown; target?: unknown; targetId?: unknown; data?: unknown };
  const raw = Array.isArray(box.actions) ? box.actions : box.actions ? [box.actions] : [];
  const list = raw.length > 0 ? raw : [box];
  return list.flatMap((item) => {
    if (!item || typeof item !== 'object') return [];
    const a = item as { action?: unknown; target?: unknown; targetId?: unknown; data?: unknown };
    if (typeof a.action !== 'string' || typeof a.target !== 'string') return [];
    return [{
      action: a.action,
      target: a.target,
      ...(typeof a.targetId === 'string' ? { targetId: a.targetId } : {}),
      ...(a.data && typeof a.data === 'object' ? { data: a.data as Record<string, unknown> } : {}),
    }];
  });
}

export function describeApproval(
  approval: ApprovalLike,
  lookupTarget?: (target: string, id: string | undefined) => string | undefined,
): ApprovalSummary {
  const args = approval.argumentsParsed;
  const description = args && typeof args === 'object'
    ? text((args as { description?: unknown }).description, 300)
    : '';

  if (approval.toolName === 'edit_chapter') {
    const a = (args ?? {}) as Record<string, unknown>;
    const chapterId = typeof a.chapterId === 'string' ? a.chapterId : undefined;
    const target = lookupTarget?.('chapter', chapterId)
      ?? (chapterId ? `章节 ${chapterId.slice(0, 8)}` : '当前章节');
    const mode = a.mode === 'append' ? '追加到正文末尾' : '替换正文';
    const fields: ApprovalField[] = [];
    if (a.title !== undefined) fields.push({ label: '新标题', value: text(a.title) });
    if (a.summary !== undefined) fields.push({ label: '新摘要', value: text(a.summary) });
    if (a.content !== undefined) {
      fields.push({ label: '写入方式', value: mode });
      fields.push({ label: '新正文', value: text(a.content, 900), long: true });
    }
    return {
      title: '她想直接修改章节',
      toolLabel: '自由编辑',
      description: description || approval.reason || '',
      items: [{ action: mode, target, fields }],
    };
  }

  if (approval.toolName === 'execute') {
    const actions = asActions(args);
    const items: ApprovalItem[] = actions.map((a) => {
      const fields: ApprovalField[] = [];
      for (const [key, value] of Object.entries(a.data ?? {})) {
        if (value === undefined) continue;
        fields.push({
          label: FIELD_LABELS[key] ?? key,
          value: text(value, key === 'content' ? 900 : 260),
          long: key === 'content',
        });
      }
      const label = lookupTarget?.(a.target, a.targetId);
      return {
        action: ACTIONS[a.action] ?? a.action,
        target: label ?? `${targetLabel(a.target)}${a.targetId ? ` ${a.targetId.slice(0, 8)}` : ''}`,
        fields,
      };
    });
    return {
      title: '她想修改创作数据',
      toolLabel: '完全授权',
      description: description || approval.reason || '',
      items: items.length > 0 ? items : [{
        action: '操作',
        target: '创作数据',
        fields: [{ label: '参数', value: text(args, 900), long: true }],
      }],
    };
  }

  return {
    title: '她请求执行一个操作',
    toolLabel: approval.toolName,
    description: approval.reason || description,
    items: [{
      action: '操作',
      target: approval.toolName,
      fields: [{ label: '请求内容', value: text(args ?? approval.argumentsText, 1200), long: true }],
    }],
  };
}
